import { Actor } from "./actor";
import { Player } from "./player";
import { Text } from "./text";
import { EVENTS_NAME } from "../consts.ts";
import { EventBus } from "../EventBus.ts";

// ── Monster type tuning ────────────────────────────────────────────────────
// The dangerous mobs are INFECTED AUTONOMOUS CARS (the 2026 fleet arc).
// Behavior profiles carry over from game24's Enemy / Enemy2 / Enemy3 (16px
// world values doubled). Type ids match the SDMonster catalog in
// static_data.json — the server sends them as SupportMob.tiled_id, and the
// display NAME comes from the server (Schmesla / ModelT9 / LOLvo).
export interface MonsterTypeCfg {
  texture: string; // top-down car sprite, facing UP (rotated to heading)
  scale: number; // car sprites are ~70x130 native; players are 32x64
  aggroRadius: number; // px — start chasing inside this
  speed: number; // px/s chase speed
  touchDamage: number; // damage per landed attack (1s cooldown)
  /** If set, touch damage is this FRACTION of the player's max health rather
   *  than the flat `touchDamage` above (which then acts as a floor). Used by
   *  the finale boss so "a third of your health bar" stays true even if
   *  starting health is retuned in configs/game.json — a hardcoded number
   *  would silently become a different fraction. */
  touchDamageFraction?: number;
  meleeRange: number; // px — how close the player must be to punch it
  attackRange: number; // px — close enough to strike, triggers the pullback
  pullbackRadius: number; // px — how far it withdraws between strikes
  pauseMs: number; // stunned pause when the target moves mid-sequence
  // Ranged types only: dumb-fire projectiles at the LOCAL player. Simulated
  // per-client (like touch damage — victim-authoritative), so no protocol.
  projectile?: {
    damage: number;
    speed: number; // px/s
    intervalMs: number; // firing cadence
    range: number; // px — only fires when the local player is inside this
  };
}

// NB `aggroRadius` is hard-capped by the controller lease at player_aoa
// (800px, configs/static_data.json): the server only leases a mob to a client
// that has a player inside that radius, and an unleased mob doesn't simulate
// at all. Values above 800 were therefore dead config (ModelT9 1000, LOLvo
// 3000). Schmesla's 400 was the one genuinely-short net — it is the fast
// striker and was giving up before the others even noticed you. To reach
// further than this, raise player_aoa, which also widens the per-tick
// broadcast (cost scales with players x entities in range).
export const MONSTER_TYPES: Record<number, MonsterTypeCfg> = {
  // ModelT9 — ancient, slow, relentless. Any color you like, as long as it
  // hunts you. (game24 Enemy profile: wide net, low speed.)
  6001: {
    texture: "mob-modelt9",
    scale: 0.7,
    aggroRadius: 800,
    speed: 100,
    touchDamage: 5,
    meleeRange: 64,
    attackRange: 48,
    pullbackRadius: 110,
    pauseMs: 700,
  },
  // Schmesla — fast striker on Full Self-Delusion mode. (game24 Enemy2.)
  6002: {
    texture: "mob-schmesla",
    scale: 0.7,
    aggroRadius: 780,
    speed: 300,
    touchDamage: 10,
    meleeRange: 64,
    attackRange: 48,
    pullbackRadius: 140,
    pauseMs: 700,
  },
  // LOLvo — a station wagon built like a bunker: barely moves, soaks
  // damage, and its "collision avoidance" fires AT you. Rapid dumb-fire
  // projectiles; strafe and close the distance.
  6003: {
    texture: "mob-lolvo",
    scale: 0.85,
    aggroRadius: 800,
    speed: 14,
    touchDamage: 15,
    meleeRange: 72,
    attackRange: 56,
    pullbackRadius: 90,
    pauseMs: 700,
    projectile: { damage: 25, speed: 240, intervalMs: 650, range: 700 },
  },
  // THE MERGER (6099) — the one-time scheduled finale boss. Enormous, does
  // NOT aggro, and is moved entirely by the server (ro-game's wander pass),
  // so aggroRadius/speed/pullback here are inert: the server never grants a
  // controller lease for this id, so the client only ever renders it in
  // remote/velocity-follow mode. What DOES apply is touch damage, which is
  // victim-authoritative and therefore still evaluated on each client.
  // A third of your health bar per hit: three touches kill, so this has to
  // be fought hit-and-run, never leaned on.
  6099: {
    texture: "mob-lolvo",
    scale: 2.6,
    aggroRadius: 0,
    speed: 0,
    touchDamage: 33,
    touchDamageFraction: 1 / 3,
    meleeRange: 150,
    attackRange: 120,
    pullbackRadius: 0,
    pauseMs: 0,
  },
};

const PLAYER_ATTACK_DAMAGE = 25;
const REPORT_INTERVAL_MS = 300; // controller position reports (like PlayerMove)
const TOUCH_COOLDOWN_MS = 1000;
// Engine audio: revs repeat while the car moves, volume falls off with
// distance to the local player (positional audio on the cheap).
const ENGINE_MAX_VOLUME = 0.35;
const ENGINE_FULL_DIST = 120; // full volume inside this
const ENGINE_SILENT_DIST = 520; // inaudible beyond this
// If the target strays this far from where they stood when the attack
// sequence began, the monster loses the thread and pauses.
//
// ⚠️ This was 28px, which broke chasing entirely: a player runs at up to
// 220px/s, so they cross 28px in ~130ms — barely 8 frames. Every attack
// sequence therefore hit `withdraw` -> instantly detected "target moved" ->
// `pause` for a full 2s. Against anyone who kept moving the car spent most
// of its life stationary and never completed a pull-back, which reads as
// "the monsters don't chase". The threshold has to mean "they RELOCATED",
// not "they took a step".
const TARGET_MOVE_EPS = 180;

type AiState = "chase" | "withdraw" | "pause";

// Seconds of movement to look ahead when testing a safe-zone boundary. One
// frame at 60fps plus margin, so a car at full speed stops short of the line
// rather than clipping a corner before the check catches it.
const LOOKAHEAD_S = 0.05;

// THE MERGER. Mirrors BOSS_MONSTER_ID in ro-game/src/lib.rs.
const BOSS_TYPE_ID = 6099;

// The scene shape Monster needs (GameMain) — avoids a circular import.
interface MonsterScene extends Phaser.Scene {
  player1: Player;
  players: Record<string, Player>;
}

export class Monster extends Actor {
  serverIid: number;
  typeId: number;
  cfg: MonsterTypeCfg;
  mobName: string;
  maxHealth: number;
  // Whether THIS client holds the server's controller lease (simulates
  // aggro/movement). Everyone else renders kinematically.
  controlled = false;

  private serverX: number;
  private serverY: number;
  private nameTag: Text;
  private healthBar: Phaser.GameObjects.Graphics;
  private pulseTween?: Phaser.Tweens.Tween;
  private lastReportMs = 0;
  private lastReportedX = Number.NaN;
  private lastReportedY = Number.NaN;
  private lastTouchMs = 0;
  private nextSlitherMs = 0;
  private nextShotMs = 0;
  private liveProjectiles: Phaser.Physics.Arcade.Image[] = [];
  private overlayOffset = 40;
  // ── attack-cycle state machine (controlled mode only) ──
  private aiState: AiState = "chase";
  private withdrawX = 0;
  private withdrawY = 0;
  private pauseUntil = 0;
  private seqTargetX = 0;
  private seqTargetY = 0;
  // ── wander (no aggro target in range) ──
  private hasWanderTarget = false;
  private wanderTargetX = 0;
  private wanderTargetY = 0;
  private wanderIdleUntil = 0;
  private wanderStuckCheckAt = 0;
  private wanderStuckLastDist = Number.POSITIVE_INFINITY;

  constructor(
    scene: MonsterScene,
    serverIid: number,
    typeId: number,
    name: string,
    x: number,
    y: number,
    health: number,
    maxHealth: number,
  ) {
    super(scene, x, y, (MONSTER_TYPES[typeId] ?? MONSTER_TYPES[6001]).texture);
    this.serverIid = serverIid;
    this.typeId = typeId;
    this.cfg = MONSTER_TYPES[typeId] ?? MONSTER_TYPES[6001];
    this.mobName = name;
    this.health = health;
    this.maxHealth = maxHealth;
    this.serverX = x;
    this.serverY = y;
    this.setScale(this.cfg.scale);
    // Physics body: a square around the car's center — the sprite ROTATES to
    // its heading, and arcade bodies don't rotate, so a square keeps contact
    // behavior consistent at any angle.
    const side = Math.min(this.width, this.height) * 0.9;
    this.getBody().setSize(side, side);
    // Overlay anchor: constant offset so the name/health bar doesn't bounce
    // as the car rotates (displayWidth/Height swap with heading).
    this.overlayOffset = (Math.max(this.width, this.height) * this.cfg.scale) / 2 + 8;
    this.nameTag = new Text(scene, x, y, this.mobName).setFontSize(14);
    this.nameTag.setColor("#ff6666");
    this.nameTag.setOrigin(0.5, 1);
    this.nameTag.setDepth(1000);
    this.healthBar = scene.add.graphics();
    this.healthBar.setDepth(1000);
    // Danger pulse: a slow, permanent "I will hurt you" throb.
    this.pulseTween = scene.tweens.add({
      targets: this,
      scale: this.cfg.scale * 1.09,
      duration: 550,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });
    // Player attack (spacebar / mobile double-tap) — same channel game24
    // used. Server-authoritative: in range -> DamageMob; the server decides
    // health/death/reward.
    this.scene.game.events.on(EVENTS_NAME.attack, this.attackHandler, this);
    this.on("destroy", () => {
      this.scene?.game?.events?.removeListener(EVENTS_NAME.attack, this.attackHandler, this);
    });
  }

  /** Server state landed (SupportMobInfo). Position is authoritative for
   *  everyone EXCEPT the controller (who is producing it). */
  applyServerState(x: number, y: number, health: number): void {
    this.serverX = x;
    this.serverY = y;
    if (health < this.health) {
      // took a hit (possibly from another player) — flinch for everyone
      this.damagePulse(false);
    }
    this.health = health;
  }

  /** Red flash + hit sound. withSound=false when the flinch is a server echo
   *  of our own already-sounded hit. */
  damagePulse(withSound: boolean): void {
    this.setTintFill(0xff3333);
    this.scene?.time.delayedCall(110, () => {
      if (this.active) this.clearTint();
    });
    if (withSound && this.scene?.sound) {
      this.scene.sound.play("sfx-mob-hit", { volume: 0.5 });
    }
  }

  preUpdate(time: number, delta: number): void {
    super.preUpdate(time, delta);
    if (this.controlled) {
      this.simulate(time);
      this.report(time);
    } else {
      this.followServer();
    }
    this.faceHeading();
    this.engine(time);
    this.shoot(time);
    this.drawOverlay();
  }

  /** Point the car along its velocity (sprites face UP at rotation 0). Keeps
   *  the last heading while stopped. */
  private faceHeading(): void {
    const body = this.getBody();
    if (!body) return;
    const vx = body.velocity.x;
    const vy = body.velocity.y;
    if (Math.abs(vx) > 3 || Math.abs(vy) > 3) {
      this.setRotation(Math.atan2(vy, vx) + Math.PI / 2);
    }
  }

  /** Ranged attack (Dalek): dumb-fire an orb at the LOCAL player's current
   *  position. Runs on EVERY client against its own player — same
   *  victim-authoritative model as touch damage, so no controller routing
   *  and no new protocol. Straight-line shots are dodgeable by strafing. */
  private shoot(time: number): void {
    const p = this.cfg.projectile;
    if (!p || time < this.nextShotMs) return;
    const scene = this.scene as MonsterScene;
    const me = scene.player1;
    if (!me || !me.active) return;
    const d = Phaser.Math.Distance.Between(this.x, this.y, me.x, me.y);
    if (d > p.range || d < 8) return;
    this.nextShotMs = time + p.intervalMs;

    const orb = scene.physics.add.image(this.x, this.y, "projectileOrb");
    orb.setDepth(900);
    this.liveProjectiles.push(orb);
    const dx = me.x - this.x;
    const dy = me.y - this.y;
    (orb.body as Phaser.Physics.Arcade.Body).setVelocity(
      (dx / d) * p.speed,
      (dy / d) * p.speed,
    );
    if (this.scene.sound) {
      // quieter with distance, like the slither
      const vol = Math.max(0.12, 0.45 * (1 - d / p.range));
      this.scene.sound.play("sfx-mob-pew", { volume: vol });
    }
    // muzzle flare
    this.setTintFill(0xffcc55);
    this.scene.time.delayedCall(70, () => {
      if (this.active) this.clearTint();
    });

    const cleanup = () => {
      this.liveProjectiles = this.liveProjectiles.filter((o) => o !== orb);
      if (orb.active) orb.destroy();
    };
    // hit MY player -> big damage (victim-authoritative, like touch)
    scene.physics.add.overlap(me, orb, () => {
      cleanup();
      me.animateDamage(p.damage);
      me.setTintFill(0xff2222);
      this.scene?.time.delayedCall(140, () => {
        if (me.active) me.clearTint();
      });
      if (this.scene?.sound) {
        this.scene.sound.play("sfx-player-hit", { volume: 0.6 });
      }
      EventBus.emit(EVENTS_NAME.playerDamaged, { damage: p.damage });
    });
    // walls stop it
    const walls = (scene as unknown as { wallsLayer?: Phaser.Tilemaps.TilemapLayer })
      .wallsLayer;
    if (walls) {
      scene.physics.add.collider(orb, walls, cleanup);
    }
    // and it burns out after 3s regardless
    this.scene.time.delayedCall(3000, cleanup);
  }

  /** Engine audio: a rev fires on a jittered beat while the car is actually
   *  moving (controlled OR remote — both set velocity), with the volume
   *  falling off by distance to the LOCAL player. Pitch varies per rev, and
   *  the fast Schmesla revs higher than the lumbering LOLvo. */
  private engine(time: number): void {
    if (time < this.nextSlitherMs) return;
    const body = this.getBody();
    if (!body || (Math.abs(body.velocity.x) < 5 && Math.abs(body.velocity.y) < 5)) {
      return;
    }
    // jittered per-car cadence so a pack doesn't rev in unison
    this.nextSlitherMs = time + 550 + Math.random() * 350;
    const me = (this.scene as MonsterScene).player1;
    if (!me || !this.scene.sound) return;
    const d = Phaser.Math.Distance.Between(this.x, this.y, me.x, me.y);
    if (d >= ENGINE_SILENT_DIST) return;
    const falloff =
      d <= ENGINE_FULL_DIST
        ? 1
        : 1 - (d - ENGINE_FULL_DIST) / (ENGINE_SILENT_DIST - ENGINE_FULL_DIST);
    // faster car = higher-revving engine (speed 14 -> ~0.85x, 300 -> ~1.35x)
    const speedRate = 0.85 + Math.min(0.5, this.cfg.speed / 600);
    this.scene.sound.play("sfx-mob-engine", {
      volume: ENGINE_MAX_VOLUME * falloff,
      rate: speedRate + Math.random() * 0.12,
    });
  }

  private drawOverlay(): void {
    this.nameTag.setPosition(this.x, this.y - this.overlayOffset - 5);
    const w = 52;
    const h = 7;
    const x0 = this.x - w / 2;
    const y0 = this.y - this.overlayOffset - 2;
    const frac = Math.max(0, Math.min(1, this.health / this.maxHealth));
    this.healthBar.clear();
    this.healthBar.fillStyle(0x220000, 0.85);
    this.healthBar.fillRect(x0, y0, w, h);
    this.healthBar.fillStyle(0xff2222, 1);
    this.healthBar.fillRect(x0, y0, w * frac, h);
    this.healthBar.lineStyle(1, 0x000000, 0.9);
    this.healthBar.strokeRect(x0, y0, w, h);
  }

  /** Controlled-mode AI: chase the nearest visible player; on reaching
   *  attack range, strike (the touch overlap does the damage) and withdraw
   *  to a random point at pullbackRadius, then come back for more. If the
   *  target moves mid-sequence, lose the thread and pause for pauseMs. */
  private simulate(time: number): void {
    const scene = this.scene as MonsterScene;
    const candidates: Player[] = [scene.player1, ...Object.values(scene.players || {})].filter(
      (p) => p && p.active,
    );
    let target: Player | null = null;
    let best = Number.POSITIVE_INFINITY;
    for (const p of candidates) {
      const d = Phaser.Math.Distance.Between(this.x, this.y, p.x, p.y);
      if (d < best) {
        best = d;
        target = p;
      }
    }
    if (!target || best >= this.cfg.aggroRadius) {
      // "chase" is the re-entry state once something comes back into aggro
      // range; until then, wander instead of idling at the spawn point.
      this.aiState = "chase";
      this.wander(time);
      return;
    }
    // Aggro reacquired mid-wander — drop any wander target so it doesn't
    // resume a stale patrol leg the instant it loses the player again.
    this.hasWanderTarget = false;

    switch (this.aiState) {
      case "chase": {
        if (best <= this.cfg.attackRange) {
          // strike lands via the touch overlap; begin the pull-back
          this.seqTargetX = target.x;
          this.seqTargetY = target.y;
          const angle = Math.random() * Math.PI * 2;
          this.withdrawX = target.x + Math.cos(angle) * this.cfg.pullbackRadius;
          this.withdrawY = target.y + Math.sin(angle) * this.cfg.pullbackRadius;
          this.aiState = "withdraw";
          this.getBody().setVelocity(0, 0);
        } else {
          this.moveToward(target.x, target.y, this.cfg.speed);
        }
        break;
      }
      case "withdraw": {
        // target relocating mid-sequence? lose the thread, take a beat
        if (
          Phaser.Math.Distance.Between(target.x, target.y, this.seqTargetX, this.seqTargetY) >
          TARGET_MOVE_EPS
        ) {
          this.aiState = "pause";
          this.pauseUntil = time + this.cfg.pauseMs;
          this.getBody().setVelocity(0, 0);
          break;
        }
        const dw = Phaser.Math.Distance.Between(this.x, this.y, this.withdrawX, this.withdrawY);
        if (dw < 10) {
          this.aiState = "chase"; // wound up — go again
        } else {
          this.moveToward(this.withdrawX, this.withdrawY, this.cfg.speed);
        }
        break;
      }
      case "pause": {
        this.getBody().setVelocity(0, 0);
        // Cut the pause short if the target has closed back in — standing
        // still while being hit looks broken, and the pause exists to give a
        // FLEEING player breathing room, not to gift a melee attacker a
        // free stationary punching bag.
        if (time >= this.pauseUntil || best <= this.cfg.attackRange * 1.5) {
          this.aiState = "chase";
        }
        break;
      }
    }
  }

  /** No aggro target in range: drive a continuous random patrol instead of
   *  idling at the spawn point, from the moment it spawns until it dies.
   *  Reuses `moveToward` for the actual movement, so safe-zone avoidance
   *  (and the Walls physics collider set up in GameMain) apply for free —
   *  wander is not a separate movement system, just a different target
   *  picker feeding the same mover. THE MERGER is server-driven and never
   *  reaches this (guarded in simulate/pickWanderTarget). */
  private wander(time: number): void {
    if (time < this.wanderIdleUntil) {
      this.getBody().setVelocity(0, 0);
      return;
    }
    if (!this.hasWanderTarget) {
      this.pickWanderTarget();
      if (!this.hasWanderTarget) {
        // Couldn't find a target clear of every safe zone (e.g. boxed in) —
        // wait a beat and try again next tick rather than spin every frame.
        this.wanderIdleUntil = time + 500;
        this.getBody().setVelocity(0, 0);
        return;
      }
    }
    const dist = Phaser.Math.Distance.Between(
      this.x,
      this.y,
      this.wanderTargetX,
      this.wanderTargetY,
    );
    if (dist < 16) {
      // Arrived — pause briefly (a car idling, not a car patrolling on
      // rails) before picking the next leg.
      this.hasWanderTarget = false;
      this.wanderIdleUntil = time + 1000 + Math.random() * 2000;
      this.getBody().setVelocity(0, 0);
      return;
    }
    // Stuck detection: a wander leg can aim into a wall the collider then
    // blocks (wander has no A* pathing, unlike click-to-move) — if a leg
    // hasn't meaningfully closed distance in 1.5s, it's wedged; abandon it
    // rather than pushing against geometry until the idle timer bails it
    // out anyway.
    if (time - this.wanderStuckCheckAt > 1500) {
      if (this.wanderStuckCheckAt !== 0 && this.wanderStuckLastDist - dist < 20) {
        this.hasWanderTarget = false;
        this.wanderStuckCheckAt = 0;
        this.getBody().setVelocity(0, 0);
        return;
      }
      this.wanderStuckCheckAt = time;
      this.wanderStuckLastDist = dist;
    }
    this.moveToward(this.wanderTargetX, this.wanderTargetY, this.cfg.speed);
  }

  /** Pick the next patrol leg: a short hop in a random direction from the
   *  CURRENT position (not spawn) — an unbiased drift, not a tether, so a
   *  car's patrol genuinely covers ground over its lifetime rather than
   *  circling one spot. Never targets inside a safe zone; a few retries
   *  handle the rare case of a car sitting near one. */
  private pickWanderTarget(): void {
    if (Monster.isBossType(this.typeId)) {
      return; // never reached in practice (see simulate/preUpdate), belt+braces
    }
    for (let i = 0; i < 8; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 200 + Math.random() * 400; // 200-600px per leg
      const tx = this.x + Math.cos(angle) * dist;
      const ty = this.y + Math.sin(angle) * dist;
      if (!Monster.inSafeZone(tx, ty)) {
        this.wanderTargetX = tx;
        this.wanderTargetY = ty;
        this.hasWanderTarget = true;
        this.wanderStuckCheckAt = 0;
        this.wanderStuckLastDist = Number.POSITIVE_INFINITY;
        return;
      }
    }
    // no target found this attempt; caller's idle-and-retry handles it
  }

  private moveToward(tx: number, ty: number, speed: number): void {
    const dx = tx - this.x;
    const dy = ty - this.y;
    const dist = Math.hypot(dx, dy);
    if (dist <= 2) {
      this.getBody().setVelocity(0, 0);
      return;
    }
    let vx = (dx / dist) * speed;
    let vy = (dy / dist) * speed;

    // SAFE ZONES. Refuse to drive into sanctuary. Axis-independent so the car
    // SLIDES along the boundary chasing a player who runs the edge, instead of
    // sticking dead against it — a mob frozen at an invisible wall reads as a
    // bug, one that paces the perimeter reads as menace.
    //
    // The server enforces this too (it drops any MobPositionUpdate landing
    // inside a zone); this half exists so the movement LOOKS right. Client
    // alone would be trivially patchable, server alone would jitter.
    if (!Monster.isBossType(this.typeId) && Monster.SAFE_ZONES.length) {
      // look one frame ahead, plus a little, so it stops short rather than
      // clipping a corner at speed
      const t = LOOKAHEAD_S;
      if (Monster.inSafeZone(this.x + vx * t, this.y)) vx = 0;
      if (Monster.inSafeZone(this.x, this.y + vy * t)) vy = 0;
      // diagonal squeeze: both axes clear alone but the corner is not
      if (vx !== 0 && vy !== 0 && Monster.inSafeZone(this.x + vx * t, this.y + vy * t)) {
        vy = 0;
      }
    }
    this.getBody().setVelocity(vx, vy);
  }

  /** Sanctuary rectangles, in map pixels. Populated once by
   *  BaseScene.initZones from the `Zones` layer (any zone_name starting
   *  "SAFE ZONE"). Static because every Monster shares one map. */
  static SAFE_ZONES: { x: number; y: number; w: number; h: number }[] = [];

  static inSafeZone(x: number, y: number): boolean {
    for (const z of Monster.SAFE_ZONES) {
      if (x >= z.x && x < z.x + z.w && y >= z.y && y < z.y + z.h) return true;
    }
    return false;
  }

  /** THE MERGER ignores sanctuary — the finale is meant to be inescapable.
   *  Keep in sync with BOSS_MONSTER_ID in ro-game/src/lib.rs. */
  static isBossType(typeId: number): boolean {
    return typeId === BOSS_TYPE_ID;
  }

  /** Throttled position report to the server (controller only). */
  private report(time: number): void {
    if (time - this.lastReportMs < REPORT_INTERVAL_MS) return;
    if (this.x === this.lastReportedX && this.y === this.lastReportedY) return;
    this.lastReportMs = time;
    this.lastReportedX = this.x;
    this.lastReportedY = this.y;
    EventBus.emit(EVENTS_NAME.mobMove, {
      server_iid: this.serverIid,
      x: this.x,
      y: this.y,
    });
  }

  /** Kinematic follow of the broadcast position (non-controller). */
  private followServer(): void {
    const dx = this.serverX - this.x;
    const dy = this.serverY - this.y;
    const dist = Math.hypot(dx, dy);
    if (dist > 200) {
      // way off (teleport/respawn/first sync) — snap
      this.setPosition(this.serverX, this.serverY);
      this.getBody().setVelocity(0, 0);
    } else if (dist > 4) {
      // catch-up speed: at least the type's speed, more if we're behind
      const sp = Math.max(this.cfg.speed, dist * 2);
      this.getBody().setVelocity((dx / dist) * sp, (dy / dist) * sp);
    } else {
      this.getBody().setVelocity(0, 0);
    }
  }

  /** Touch damage to the LOCAL player — victim-authoritative: each client
   *  applies and reports its own damage (mob positions are synced, so the
   *  overlap fires on the victim's client no matter who controls the mob). */
  tryTouchLocalPlayer(player: Player): void {
    const now = this.scene.time.now;
    if (now - this.lastTouchMs < TOUCH_COOLDOWN_MS) return;
    this.lastTouchMs = now;
    const damage = this.touchDamageFor(player);
    player.animateDamage(damage);
    // red hurt-pulse + thud on the victim's client
    player.setTintFill(0xff2222);
    this.scene.time.delayedCall(140, () => {
      if (player.active) player.clearTint();
    });
    if (this.scene.sound) {
      this.scene.sound.play("sfx-player-hit", { volume: 0.6 });
    }
    EventBus.emit(EVENTS_NAME.playerDamaged, { damage });
  }

  /** Flat `touchDamage`, unless the type asks for a fraction of max health
   *  (the finale boss). The flat value acts as a floor so a bad/low
   *  maxHealth reading can never make a hit trivial. */
  private touchDamageFor(player: Player): number {
    const frac = this.cfg.touchDamageFraction;
    if (!frac) return this.cfg.touchDamage;
    const scaled = Math.ceil(player.getMaxHealth() * frac);
    return Math.max(this.cfg.touchDamage, scaled);
  }

  private attackHandler = (): void => {
    const scene = this.scene as MonsterScene;
    if (!scene?.player1 || !this.active) return;
    const d = Phaser.Math.Distance.Between(this.x, this.y, scene.player1.x, scene.player1.y);
    if (d < this.cfg.meleeRange) {
      // immediate local juice; the server adjudicates health/death/reward
      this.damagePulse(true);
      EventBus.emit(EVENTS_NAME.mobDamage, {
        server_iid: this.serverIid,
        damage: PLAYER_ATTACK_DAMAGE,
      });
    }
  };

  destroy(fromScene?: boolean): void {
    this.pulseTween?.remove();
    this.nameTag?.destroy();
    this.healthBar?.destroy();
    this.liveProjectiles.forEach((o) => {
      if (o.active) o.destroy();
    });
    this.liveProjectiles = [];
    super.destroy(fromScene);
  }
}
